/**
 * P0 の地形：LOD なしのチャンク分割メッシュ（80m 間隔）。色は地形の種類と傾き・標高から頂点色で付ける。
 * LOD と地面のシェーダーは P2・P4（docs/05_rendering-and-audio.md 3 章）。
 */
import * as THREE from 'three';
import { Biome, type WorldData } from '../../core/world';

const BIOME_COLOR: Record<number, THREE.Color> = {
  [Biome.Lake]: new THREE.Color(0x3a5f78),
  [Biome.Road]: new THREE.Color(0xb8ab8c),
  [Biome.Meadow]: new THREE.Color(0x7e9a55),
  [Biome.Forest]: new THREE.Color(0x3a5a34),
  [Biome.Wetland]: new THREE.Color(0x5f7550),
  [Biome.Scree]: new THREE.Color(0x9c978d),
  [Biome.Rock]: new THREE.Color(0x77706a),
  [Biome.Snow]: new THREE.Color(0xf2f5fa),
  [Biome.Cliff]: new THREE.Color(0x3d3431),
};

/** 描画の頂点間隔（最終解像度の何セルごとか） */
const STEP = 2;
/** 1 チャンクの頂点の一辺（セル数 = CHUNK） */
const CHUNK = 128;

export function createTerrain(world: WorldData): THREE.Group {
  const group = new THREE.Group();
  group.name = 'terrain';
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 });

  const vx = Math.floor((world.nx - 1) / STEP) + 1;
  const vz = Math.floor((world.nz - 1) / STEP) + 1;
  const H = (i: number, j: number): number => {
    const ci = Math.min(Math.max(i, 0), world.nx - 1);
    const cj = Math.min(Math.max(j, 0), world.nz - 1);
    return world.height[cj * world.nx + ci] as number;
  };
  const tmp = new THREE.Color();
  const grassHigh = new THREE.Color(0xa8a868);

  for (let cz = 0; cz < vz - 1; cz += CHUNK) {
    for (let cx = 0; cx < vx - 1; cx += CHUNK) {
      const w = Math.min(CHUNK, vx - 1 - cx) + 1;
      const d = Math.min(CHUNK, vz - 1 - cz) + 1;
      const positions = new Float32Array(w * d * 3);
      const normals = new Float32Array(w * d * 3);
      const colors = new Float32Array(w * d * 3);
      for (let j = 0; j < d; j++) {
        for (let i = 0; i < w; i++) {
          const gi = (cx + i) * STEP;
          const gj = (cz + j) * STEP;
          const o = (j * w + i) * 3;
          const h = H(gi, gj);
          positions[o] = gi * world.cellM;
          positions[o + 1] = h;
          positions[o + 2] = gj * world.cellM;
          // 法線は全体の高さから求める（チャンクの境目に段差を出さない）
          const dx = (H(gi + STEP, gj) - H(gi - STEP, gj)) / (2 * STEP * world.cellM);
          const dz = (H(gi, gj + STEP) - H(gi, gj - STEP)) / (2 * STEP * world.cellM);
          const inv = 1 / Math.hypot(dx, 1, dz);
          normals[o] = -dx * inv;
          normals[o + 1] = inv;
          normals[o + 2] = -dz * inv;

          const b = world.biome[gj * world.nx + gi] as number;
          tmp.copy(BIOME_COLOR[b] ?? (BIOME_COLOR[Biome.Meadow] as THREE.Color));
          if (b === Biome.Meadow) tmp.lerp(grassHigh, Math.min(1, Math.max(0, (h - 1500) / 1000)));
          // 急斜面は岩が透ける
          const slope = Math.hypot(dx, dz);
          if (b !== Biome.Lake && b !== Biome.Road)
            tmp.lerp(BIOME_COLOR[Biome.Rock] as THREE.Color, Math.min(0.5, Math.max(0, (slope - 0.6) * 0.8)));
          colors[o] = tmp.r;
          colors[o + 1] = tmp.g;
          colors[o + 2] = tmp.b;
        }
      }
      const index: number[] = [];
      for (let j = 0; j < d - 1; j++) {
        for (let i = 0; i < w - 1; i++) {
          const a = j * w + i;
          const b = a + 1;
          const c = a + w;
          const e = c + 1;
          index.push(a, c, b, b, c, e);
        }
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geo.setIndex(index);
      geo.computeBoundingSphere();
      group.add(new THREE.Mesh(geo, material));
    }
  }
  return group;
}

/**
 * 描画している地面の高さ。メッシュと同じ三角形で補間するので、選手やカメラを置いても浮いたり埋まったりしない
 * （sim の高さは 40m 格子の双線形補間で、80m 間隔のメッシュとは最大で数十 m ずれる）。
 */
export function renderedHeightAt(world: WorldData, x: number, z: number): number {
  const s = STEP * world.cellM;
  const vx = Math.floor((world.nx - 1) / STEP);
  const vz = Math.floor((world.nz - 1) / STEP);
  const fx = Math.min(Math.max(x / s, 0), vx);
  const fz = Math.min(Math.max(z / s, 0), vz);
  const i = Math.min(Math.floor(fx), vx - 1);
  const j = Math.min(Math.floor(fz), vz - 1);
  const u = fx - i;
  const v = fz - j;
  const H = (ii: number, jj: number): number => world.height[jj * STEP * world.nx + ii * STEP] as number;
  const ha = H(i, j);
  const hb = H(i + 1, j);
  const hc = H(i, j + 1);
  const he = H(i + 1, j + 1);
  // メッシュの三角形は (a, c, b) と (b, c, e)。対角線は b–c
  return u + v <= 1 ? ha + (hb - ha) * u + (hc - ha) * v : he + (hc - he) * (1 - u) + (hb - he) * (1 - v);
}

/**
 * 折れ線（x, y, z の並び）を、描画している地面にぴったり沿わせる。
 * メッシュの三角形の境目（格子の縦・横の線と対角線）で区切り、区切った点の高さを地面に合わせる
 * （三角形の中では地面は平らなので、これで線が地面に潜ったり浮いたりしない）。y は元の値を捨てて地面の高さにする。
 */
export function drapePolyline(world: WorldData, pts: readonly number[]): number[] {
  const s = STEP * world.cellM;
  const out: number[] = [];
  const ts: number[] = [];
  for (let i = 0; i + 3 < pts.length; i += 3) {
    const x0 = pts[i] as number;
    const z0 = pts[i + 2] as number;
    const x1 = pts[i + 3] as number;
    const z1 = pts[i + 5] as number;
    const fx0 = x0 / s;
    const fz0 = z0 / s;
    const fx1 = x1 / s;
    const fz1 = z1 / s;
    ts.length = 0;
    ts.push(0, 1);
    const cross = (a: number, b: number): void => {
      if (Math.abs(b - a) < 1e-9) return;
      for (let k = Math.ceil(Math.min(a, b)); k <= Math.floor(Math.max(a, b)); k++) {
        const t = (k - a) / (b - a);
        if (t > 0 && t < 1) ts.push(t);
      }
    };
    cross(fx0, fx1);
    cross(fz0, fz1);
    cross(fx0 + fz0, fx1 + fz1);
    ts.sort((a, b) => a - b);
    let last = -1;
    for (const t of ts) {
      if (t - last < 1e-6 || (i > 0 && t === 0)) continue;
      last = t;
      const x = x0 + (x1 - x0) * t;
      const z = z0 + (z1 - z0) * t;
      out.push(x, renderedHeightAt(world, x, z), z);
    }
  }
  return out;
}

/**
 * 地面に沿わせた線を浮かせる高さ（m）。地面すれすれだと遠くから見たときに地面とちらつくので、
 * カメラが地面から高いほど大きく浮かせる（追従では約 0.3m、上空の地図では数十 m）。
 */
export function overlayLift(cameraHeightAboveGround: number): number {
  return Math.min(60, Math.max(0.3, 0.2 + cameraHeightAboveGround * 0.0018));
}

export function createLakes(world: WorldData): THREE.Group {
  const group = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({ color: 0x2f5f80, roughness: 0.15, metalness: 0.1 });
  for (const l of world.lakes) {
    const shape = new THREE.Shape();
    shape.absellipse(0, 0, l.radiusX * 1.12, l.radiusZ * 1.25, 0, Math.PI * 2, false, 0);
    const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape, 64), material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(l.center.x, l.levelM, l.center.z);
    group.add(mesh);
  }
  return group;
}
